import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDate } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
    Badge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@devhop/ui";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import {
    ArrowLeftIcon,
    ArrowUpRightIcon,
    CalendarIcon,
    ClockIcon,
    EditIcon,
    MailIcon,
    PhoneIcon,
    UserIcon,
    WalletIcon,
} from "lucide-react";
import { usePerformerQuery } from "../api/queries";
import { ROLE_FEE_KIND_LABELS } from "../../domain/contracts";

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? "").toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof PhoneIcon;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-muted-foreground text-xs uppercase tracking-wide">
          {label}
        </p>
        <div className="mt-0.5 text-sm">{children}</div>
      </div>
    </div>
  );
}

export function PerformerDetailPage() {
  const { id } = useParams({ from: "/app/performance/performers/$id" });
  const nav = useNavigate({ from: "/app/performance/performers/$id" });
  const { data: performer, isLoading } = usePerformerQuery(id);
  const canManage = useActionPermission(["performers:update"]);

  if (isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ນັກສະແດງ", to: "/app/performance/performers" },
              { label: "..." },
            ]}
          />
        </Header>
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫລດ...</p>
        </Main>
      </>
    );
  }

  if (!performer) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ນັກສະແດງ", to: "/app/performance/performers" },
              { label: "ບໍ່ພົບ" },
            ]}
          />
        </Header>
        <Main>
          <p className="text-destructive">ບໍ່ພົບນັກສະແດງ</p>
        </Main>
      </>
    );
  }

  const isActive = performer.status === "active";

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ນັກສະແດງ", to: "/app/performance/performers" },
            { label: performer.name },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl tracking-tight md:text-3xl">
              {performer.name}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant={isActive ? "default" : "secondary"}>
                {isActive ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
              </Badge>
              <span className="text-muted-foreground text-sm">
                ອັບເດດ{" "}
                {formatDistanceToNow(new Date(performer.updatedAt), {
                  addSuffix: true,
                })}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/performance/performers" })}
            >
              <ArrowLeftIcon className="size-4" /> ກັບຄືນ
            </Button>
            {canManage && (
              <Button
                onClick={() =>
                  nav({
                    to: "/app/performance/performers/$id/edit",
                    params: { id },
                  })
                }
              >
                <EditIcon className="size-4" /> ແກ້ໄຂ
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="h-fit lg:col-span-1">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted font-semibold text-2xl text-muted-foreground">
                  {getInitials(performer.name)}
                </div>
                <p className="mt-4 font-semibold text-lg">{performer.name}</p>
                <Badge
                  className="mt-2"
                  variant={isActive ? "default" : "secondary"}
                >
                  {isActive ? "ພ້ອມຈ້າງ" : "ບໍ່ໃຊ້ງານ"}
                </Badge>
              </div>

              <div className="mt-6 space-y-4 border-t pt-6">
                <DetailRow icon={PhoneIcon} label="ເບີໂທ">
                  {performer.phoneNumber ? (
                    <a
                      href={`tel:${performer.phoneNumber}`}
                      className="font-medium hover:underline"
                    >
                      {performer.phoneNumber}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </DetailRow>

                <DetailRow icon={MailIcon} label="ອີເມລ">
                  {performer.email ? (
                    <a
                      href={`mailto:${performer.email}`}
                      className="break-all font-medium hover:underline"
                    >
                      {performer.email}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </DetailRow>
              </div>

              <div className="mt-6 space-y-4 border-t pt-6">
                <DetailRow icon={UserIcon} label="ບົດບາດເລີ່ມຕົ້ນ">
                  <span className="font-medium">
                    {ROLE_FEE_KIND_LABELS[performer.defaultRoleFeeKind] ??
                      performer.defaultRoleFeeKind}
                  </span>
                </DetailRow>
              </div>

              <div className="mt-6 space-y-4 border-t pt-6">
                <p className="flex items-center gap-2 font-medium text-sm">
                  <WalletIcon className="size-4 text-primary" />
                  ບັນຊີຮັບເງິນ
                </p>
                <DetailRow icon={WalletIcon} label="ທະນາຄານ">
                  {performer.bankName ? (
                    <span className="font-medium">{performer.bankName}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </DetailRow>
                <DetailRow icon={WalletIcon} label="ເລກບັນຊີ">
                  {performer.bankAccount ? (
                    <span className="font-medium">{performer.bankAccount}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </DetailRow>
                <DetailRow icon={UserIcon} label="ຊື່ບັນຊີ">
                  {performer.accountName ? (
                    <span className="font-medium">{performer.accountName}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </DetailRow>
              </div>

              <div className="mt-6 space-y-3 border-t pt-6 text-muted-foreground text-xs">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="size-3.5 shrink-0" />
                  <span>ສ້າງເມື່ອ {formatDate(performer.createdAt)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <ClockIcon className="size-3.5 shrink-0" />
                  <span>ອັບເດດ {formatDate(performer.updatedAt)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6 lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  ໝາຍເຫດດ້ານການດຳເນີນງານ
                </CardTitle>
              </CardHeader>
              <CardContent>
                {performer.notes ? (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">
                    {performer.notes}
                  </p>
                ) : (
                  <p className="text-muted-foreground text-sm italic">
                    ຍັງບໍ່ມີໝາຍເຫດ
                  </p>
                )}
              </CardContent>
            </Card>

            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">ຄູສອນທີ່ເຊື່ອມຕໍ່</CardTitle>
                </CardHeader>
                <CardContent>
                  {performer.instructorName && performer.instructorId ? (
                    <Link
                      to="/app/master/instructors/$id"
                      params={{ id: performer.instructorId }}
                      className="group flex items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3 transition-colors hover:bg-muted/50"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                        <UserIcon className="size-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm">
                          {performer.instructorName}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          ເບິ່ງໂປຣໄຟລ໌ຄູສອນ
                        </p>
                      </div>
                      <ArrowUpRightIcon className="size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                    </Link>
                  ) : performer.instructorName ? (
                    <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                        <UserIcon className="size-4 text-muted-foreground" />
                      </div>
                      <span className="font-medium text-sm">
                        {performer.instructorName}
                      </span>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed px-4 py-6 text-center text-muted-foreground text-sm">
                      ຍັງບໍ່ໄດ້ເຊື່ອມຕໍ່ຄູສອນ
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">ການຄິດເງິນຄ່າຈ້າງ</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    ອັດຕາຄ່າຈ້າງຄິດຈາກບົດບາດນັກສະແດງ ຫຼື ອອກແບບທ່າເຕັ້ນ ເມື່ອຈັດທີມໃນງານສະແດງ
                    ໂດຍລະບົບຈະຄຳນວນຄ່າໂຊ ແລະ ຄ່າຊ້ອມອັດຕະໂນມັດ
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </Main>
    </>
  );
}
